import React from 'react';
import {createPortal} from 'react-dom';
import cx from 'classnames';
import {withSlotsPlaceholders} from '@wix/widget-plugins-ooi';
import {withExpressCheckoutButtons} from '@wix/ecom-platform-express-checkout-buttons/widget';
import s from './ProductPageApp.scss';
import {LayoutId, LayoutNames} from '../../constants';
import {ProductPageAppProps} from '../../types/app-types';
import {TPAComponentsProvider, Toast, ToastPlacement, ToastSkin} from '@wix/wix-ui-tpa/cssVars';
import {SuggestedCurrencyAutoSwitchToastRenderer} from '@wix/wixstores-client-storefront-common';
import {withGlobalProps} from '../../providers/globalPropsProvider';
import '../../styles/app.global.scss';
import {getProductPageSettingsFromProps} from '../../../commons/styleParamsService';
import {EmptyState} from '../Layouts/EmptyState/EmptyState';
import {ClassicLayout} from '../Layouts/ClassicLayout/ClassicLayout';
import {QuickViewLayout} from '../Layouts/QuickViewLayout/QuickViewLayout';
import {ResponsiveLayout} from '../Layouts/ResponsiveLayout/ResponsiveLayout';
import {SimpleLayout} from '../Layouts/SimpleLayout/SimpleLayout';
import {SleekLayout} from '../Layouts/SleekLayout/SleekLayout';
import {SpotlightLayout} from '../Layouts/SpotlightLayout/SpotlightLayout';
import {StunningLayout} from '../Layouts/StunningLayout/StunningLayout';
import {SPECS} from '../../specs';

const layouts = {
  [LayoutId.EmptyState]: EmptyState,
  [LayoutId.Classic]: ClassicLayout,
  [LayoutId.QuickView]: QuickViewLayout,
  [LayoutId.Responsive]: ResponsiveLayout,
  [LayoutId.Simple]: SimpleLayout,
  [LayoutId.Sleek]: SleekLayout,
  [LayoutId.Spotlight]: SpotlightLayout,
  [LayoutId.Stunning]: StunningLayout,
};

export const LayoutContext = React.createContext('');

export const ProductPageAppDataHook = {
  Layout: 'product-layout',
  ProductPage: 'product-page',
  ErrorToast: 'product-page-error-toast',
};

class ProductPageAppBase extends React.Component<ProductPageAppProps, {isToastClosed: boolean; isMounted: boolean}> {
  public state = {
    isToastClosed: false,
    isMounted: false,
  };

  public componentDidMount(): void {
    this.setState({isMounted: true});
    this.props.host.registerToComponentDidLayout(this.reportAppLoaded);
    this.safeRun(this.props.notifyProduct);
    this.safeRun(this.props.globals.reportProductPageLoadedPanorama);
  }

  public componentDidUpdate(prevProps: Readonly<ProductPageAppProps>): void {
    if (
      this.props.globals.product &&
      (prevProps.globals.product.id !== this.props.globals.product.id ||
        prevProps.globals.isInteractive !== this.props.globals.isInteractive)
    ) {
      this.safeRun(this.props.notifyProduct);
    }

    if (
      prevProps.globals.toastErrorMessage !== this.props.globals.toastErrorMessage &&
      this.props.globals.toastErrorMessage
    ) {
      this.setState({isToastClosed: false});
    }
  }

  private readonly reportAppLoaded = () => {
    this.safeRun(this.props.globals.onAppLoaded);
  };

  private safeRun(fn) {
    if (this.props.globals.isInteractive && typeof fn === 'function') {
      fn();
    }
  }

  private renderErrorToast(): JSX.Element | null {
    if (!this.state.isMounted) {
      return null;
    }
    const {toastErrorMessage, host} = this.props.globals;
    const {isToastClosed} = this.state;
    return createPortal(
      <Toast
        data-hook={ProductPageAppDataHook.ErrorToast}
        className={cx(host.id, s.errorToast)}
        skin={ToastSkin.error}
        role="alert"
        placement={ToastPlacement.bottomFullWidth}
        isShown={!!toastErrorMessage && !isToastClosed}
        onClose={() => this.setState({isToastClosed: true})}
        shouldShowCloseButton>
        {toastErrorMessage}
      </Toast>,
      document.body
    );
  }

  public render(): JSX.Element {
    const {product, isDesktop, layoutId, isMobile, isRTL, isQuickView, isEditorX, useExperiments} = this.props.globals;
    const fakeUITPAMobileMode = isQuickView; // todo(@nitzansi) make UI TPA accept isNativeSelect in dropdown by force
    const settings = getProductPageSettingsFromProps(this.props);
    const LayoutComp: any = layouts[layoutId];
    const layoutName = LayoutNames[layoutId];
    const layoutClass = `layout__${layoutName}`;
    const classNames = cx(s.ProductPageApp, layoutClass, {
      [s.mobile]: isMobile,
      isDesktop,
      isMobile,
      isQuickView,
      responsive: isEditorX,
    });
    const isUseCartV2ForDirectPurchaseEnabled = useExperiments.enabled(SPECS.UseCartV2ForDirectPurchase);

    return (
      <TPAComponentsProvider value={{mobile: isMobile || fakeUITPAMobileMode, rtl: isRTL}}>
        <SuggestedCurrencyAutoSwitchToastRenderer intent={this.props.globals.suggestedCurrencyAutoSwitchToastIntent} />
        <LayoutContext.Provider value={layoutName}>
          <div data-hook="product-page" className={classNames} data-layout-name={layoutName}>
            <LayoutComp
              data-hook="product-layout"
              product={product}
              settings={settings}
              globals={this.props.globals}
              {...this.props}
            />
          </div>
        </LayoutContext.Provider>
        {isUseCartV2ForDirectPurchaseEnabled && this.renderErrorToast()}
      </TPAComponentsProvider>
    );
  }
}

export const ProductPageApp = withGlobalProps(withExpressCheckoutButtons(withSlotsPlaceholders(ProductPageAppBase)));
